import { View, Text, StyleSheet, Platform } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles, addColorOpacity } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { Card } from '../ui/Card';

interface MortgageCalculatorResultsProps {
  loanAmount: number;
  monthlyPayment: number;
  totalInterest: number;
  totalPaid: number;
}

export function MortgageCalculatorResults({
  loanAmount,
  monthlyPayment,
  totalInterest,
  totalPaid,
}: MortgageCalculatorResultsProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);

  return (
    <View style={styles.container}>
      <Text style={styles.sectionTitle}>Loan Summary</Text>
      
      <View style={styles.resultsGrid}>
        <Card style={styles.resultCard}>
          <Text style={styles.resultLabel}>Loan Amount</Text>
          <Text style={styles.resultValue}>${loanAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Text>
        </Card>

        <Card style={[styles.resultCard, styles.resultCardHighlight]} elevated>
          <Text style={styles.resultLabel}>Monthly Payment</Text>
          <Text style={[styles.resultValue, styles.resultValueHighlight]}>
            ${monthlyPayment.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Text>
          <Text style={styles.resultSubtext}>Principal & Interest</Text>
        </Card>

        <Card style={styles.resultCard}>
          <Text style={styles.resultLabel}>Total Interest</Text>
          <Text style={styles.resultValue}>${totalInterest.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Text>
        </Card>

        <Card style={styles.resultCard}>
          <Text style={styles.resultLabel}>Total Paid</Text>
          <Text style={styles.resultValue}>${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Text>
        </Card>
      </View>
    </View>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    container: {
      width: '100%',
    },
    sectionTitle: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.md,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: utils.typography.sizes.base * utils.typography.lineHeight.tight,
    },
    resultsGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.md,
    },
    resultCard: {
      flex: 1,
      minWidth: 140,
      padding: utils.spacing.md,
    },
    resultCardHighlight: {
      backgroundColor: tokens.color.primaryContainer,
      borderColor: tokens.color.primary,
      borderWidth: 2,
    },
    resultLabel: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      letterSpacing: 0.2,
      textTransform: 'uppercase',
    },
    resultValue: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: utils.typography.sizes.lg * utils.typography.lineHeight.tight,
    },
    resultValueHighlight: {
      fontSize: utils.typography.sizes.xl,
      color: tokens.color.primary,
      lineHeight: utils.typography.sizes.xl * utils.typography.lineHeight.tight,
    },
    resultSubtext: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: utils.typography.sizes.xs * utils.typography.lineHeight.normal,
    },
  });
}

